import { cloneDeep, get, isEqual } from 'lodash';
import type Handsontable from 'handsontable/base';
import type { Formulas } from 'handsontable/plugins';

import { CellData, CellDataChange, TableContentData } from '../../../../../common/table/TableTypes';
import { buildHotTableContentData } from './hotTableDataUtils';
import { getFirstSelection, getStartCol, getStartRow } from '../selectionUtils/tableSelectionAccessors';
import { excludeRefHeadersFromSelection, getAllCellsBetween } from '../selectionUtils/tableSelectionGeometryUtils';
import { isBackgroundStyle, isColorStyle } from '../../../../components/editor/editorUtils';
import { convertFromRaw, convertToRaw, EditorState, Modifier } from 'draft-js';
import getSelectionStateForContent from '../../../../components/editor/customRichUtils/selection/getSelectionStateForContent';
import {
    TEXT_BACKGROUND_PRESETS,
    TEXT_COLOR_PRESETS,
} from '../../../../components/editor/plugins/textColor/textColorConstants';
import { doesTableCellDataHaveInlineStyles } from '../dataUtils/cell/tableCellStyleUtils';
import rawGetText from '../../../../../common/utils/editor/rawUtils/rawGetText';
import { setHotTableCellMetaObject } from './meta/hotTableCellMetaUtils';
import { isHotInstanceAvailable } from './hotTableInstanceValidationUtils';
import { MNHotInstance } from '../../tableHotInstanceTypes';
import { updateTiptapContentForWholeCellTextStyle } from '../dataUtils/cell/tableCellTiptapContentStyleUtils';
import getEditorJsonText from '../../../../../common/tiptap/utils/jsonContentUtils/getEditorJsonText';

const EMPTY_CELL_DATA: CellData = { value: null };

/**
 * - Force Handsontable to repaint cells so metadata-only changes like background, text style, etc. become visible
 * - Recalculate table layout after the repaint, so row heights and table dimensions match the new rendered
 *   content. This matters when a change affects wrapping, rich text, renderer structure, or cell type.
 */
export const renderHotTable = (hotInstance: MNHotInstance): void => {
    // `requestAnimationFrame` is used for calling render here as it improves performance
    requestAnimationFrame(() => {
        if (!isHotInstanceAvailable(hotInstance)) return;

        hotInstance.render();
    });
};

/**
 * Returns the stored cell data for a coordinate, falling back to an empty cell shape when metadata is missing.
 */
const getHotTableCellDataForChange = (hotTableData: TableContentData, row: number, col: number): CellData =>
    cloneDeep(get(hotTableData, [row, col]) || EMPTY_CELL_DATA);

/**
 * A cell's value is stored in both cellValue (primitive value controlled by HOT) and cellMeta (POJO controlled by us).
 *
 * There are some cellMeta changes that don't require syncing to cellValue, such as cell background color or
 * cell alignment changes, but there are other changes that do:
 */
const shouldSyncCellValueToHOT = ({
    previousCellData,
    newCellData,
    formulasPlugin,
    row,
    col,
}: {
    previousCellData: CellData;
    newCellData: CellData;
    formulasPlugin: Formulas;
    row: number;
    col: number;
}): boolean => {
    // If cell value has changed, sync cellMeta.value to HOT
    const cellValueHasChanged = previousCellData.value !== newCellData.value;
    if (cellValueHasChanged) return true;

    const hyperFormulaEngine = formulasPlugin.engine;
    const sheetId = formulasPlugin.sheetId;
    if (!hyperFormulaEngine || sheetId === null) return false;

    // If cell has formula dependents, sync cellMeta.value to HOT to trigger `afterChange`,
    // which will infer cell type changes to the dependent cells.
    const cellDependents = hyperFormulaEngine.getCellDependents({ sheet: sheetId, row, col });
    return cellDependents.length > 0;
};

/**
 * Applies concrete cell-data replacements to the HOT instance without merging with existing metadata.
 *
 * NOTE: This will *not* automatically trigger a redux sync. You'll need to manually use
 *   hotInstance.runHooks('onMilanoteMarkDirty', source) after this function to trigger the sync.
 */
export const applyExactHotTableCellDataChanges = (
    hotInstance: MNHotInstance,
    cellDataChanges: CellDataChange[],
    source: string,
): void => {
    if (!cellDataChanges?.length) return;

    hotInstance.suspendRender();

    const cellValueChanges: Array<[number, number, Handsontable.CellValue]> = [];

    const formulasPlugin = hotInstance.getPlugin('formulas');

    cellDataChanges.forEach(([row, col, previousCellData, newCellData]) => {
        const numericCol = Number(col);
        const exactCellData = cloneDeep(newCellData);

        setHotTableCellMetaObject(hotInstance, row, numericCol, { cellData: exactCellData });

        if (shouldSyncCellValueToHOT({ previousCellData, newCellData, formulasPlugin, row, col: numericCol })) {
            cellValueChanges.push([row, numericCol, exactCellData.value]);
        }
    });

    // Update cells that are referenced so that cell type changes can be applied to the dependents
    // This must run after the table element has been updated, because any dependent cell type
    // changes through setDataAtCell > afterChange will also be updated through this function recursively,
    // and we don't want the inner table data updates to happen before the original one
    if (cellValueChanges.length > 0) {
        // There is a Handsontable bug where setDataAtCell only resolves source properly if passed as 4th param,
        // despite the type overloads saying otherwise
        // TODO-HANDSONTABLE-UPGRADE: Check whether this is still a bug after upgrading Handsontable
        // @ts-ignore - This addition was pulled from a bug that Marisa fixed
        hotInstance.setDataAtCell(cellValueChanges, undefined, undefined, source);
    }

    hotInstance.resumeRender();

    renderHotTable(hotInstance);
};

/**
 * Builds the full cell-data changes needed to apply or remove a text style across the current selection.
 */
export const buildHotTableTextStyleUpdate = (
    hotInstance: MNHotInstance,
    textStyle: string | null,
    cellSelections = hotInstance.getSelected(),
): CellDataChange[] | undefined => {
    if (!cellSelections) return;

    const hotTableData = buildHotTableContentData(hotInstance);

    // ****************************************************
    // Determine whether to add/remove inline style based on the first cell selection

    const firstCellSelection = getFirstSelection(cellSelections);
    if (!firstCellSelection) return;

    // Remove any header cells from the selection
    const validCellSelection = excludeRefHeadersFromSelection(firstCellSelection);

    const selectedCellData = getHotTableCellDataForChange(
        hotTableData,
        getStartRow(validCellSelection),
        getStartCol(validCellSelection),
    );

    const shouldAddInlineStyle = textStyle ? !doesTableCellDataHaveInlineStyles(selectedCellData, textStyle) : true;
    const isColorOrBackgroundTextStyleChange = !textStyle || isColorStyle(textStyle) || isBackgroundStyle(textStyle);

    // ****************************************************
    // Apply/remove inline style to all selected cells

    const cellDataChanges: CellDataChange[] = [];

    getAllCellsBetween(cellSelections).forEach(({ row, col }) => {
        const previousCellData = getHotTableCellDataForChange(hotTableData, row, col);
        const nextCellData = cloneDeep(previousCellData);

        // Add/remove text style from cellData.textStyle

        let newTextStyle = nextCellData.textStyle || [];
        if (shouldAddInlineStyle) {
            newTextStyle = newTextStyle.filter((currentTextStyle) => {
                if (isColorOrBackgroundTextStyleChange) {
                    return !(isColorStyle(currentTextStyle) || isBackgroundStyle(currentTextStyle));
                }

                return currentTextStyle !== textStyle;
            });

            textStyle && newTextStyle.push(textStyle);
        } else {
            newTextStyle = newTextStyle.filter((style) => style !== textStyle);
        }

        nextCellData.textStyle = newTextStyle;

        // Add/remove text style from inline style of cellData.draftContent
        if (previousCellData.draftContent) {
            // If a color/background text style is added, remove all other existing color/background inline text styles
            if (shouldAddInlineStyle && isColorOrBackgroundTextStyleChange) {
                let contentState = convertFromRaw(previousCellData.draftContent);
                let newEditorState = EditorState.createWithContent(contentState);

                const selectionState = getSelectionStateForContent(contentState);

                [...Object.values(TEXT_COLOR_PRESETS), ...Object.values(TEXT_BACKGROUND_PRESETS)].forEach((style) => {
                    contentState = Modifier.removeInlineStyle(contentState, selectionState, style);
                });

                newEditorState = EditorState.push(newEditorState, contentState, 'change-inline-style');

                nextCellData.draftContent = convertToRaw(newEditorState.getCurrentContent());
                nextCellData.value = rawGetText(nextCellData.draftContent);
            }

            // If the style needs to be removed, ensure that the styling is removed from Draft format as well
            if (!shouldAddInlineStyle && textStyle) {
                const previousContentState = convertFromRaw(previousCellData.draftContent);

                let newEditorState = EditorState.createWithContent(previousContentState);

                const selectionState = getSelectionStateForContent(previousContentState);

                const newContentState = Modifier.removeInlineStyle(previousContentState, selectionState, textStyle);
                newEditorState = EditorState.push(newEditorState, newContentState, 'change-inline-style');

                nextCellData.draftContent = convertToRaw(newEditorState.getCurrentContent());
                nextCellData.value = rawGetText(nextCellData.draftContent);
            }
        }

        /**
         * Tiptap-rendered cells read directly from persisted JSON marks, so keep that content in sync with
         * whole-cell toolbar styles rather than relying on the separate cell-level textStyle metadata alone.
         */
        if (previousCellData.tiptapContent) {
            nextCellData.tiptapContent = updateTiptapContentForWholeCellTextStyle({
                content: previousCellData.tiptapContent,
                textStyle,
                shouldAddInlineStyle,
                isColorOrBackgroundTextStyleChange,
            });
            nextCellData.value = getEditorJsonText(nextCellData.tiptapContent);
        }

        if (isEqual(previousCellData, nextCellData)) return;

        cellDataChanges.push([row, col, previousCellData, cloneDeep(nextCellData)]);
    });

    if (!cellDataChanges.length) return;

    return cellDataChanges;
};
